// Edit Day Modal — the OnTheClock "Adjusting day for..." equivalent.
//
// Layout (top → bottom):
//   1. Header: Date + Save button
//   2. Punches list: 1..N {in, out, after_midnight, use_hours, note, delete}
//      + "Add Punch" button to add another
//   3. Override Hours panel (TOTAL / REG / OT1 / OT2 / OT3)
//   4. PTO + Unpaid + Monetary (tip / bonus / commission)
//   5. Day Notes (free text)
//   6. Audit Log expander (read-only history)
//
// Every save prompts for a reason which is stamped into time_clock_edits.

import { useEffect, useMemo, useState } from 'react';
import { api } from '../../api';
import ModalRoot from '../../components/ModalRoot';

function secondsToHours(s) { if (s == null) return ''; return (s / 3600).toFixed(2); }
function hoursToSeconds(h) { if (h === '' || h == null) return null; const n = parseFloat(h); return isNaN(n) ? null : Math.round(n * 3600); }
function centsToDollars(c) { if (c == null) return ''; return (c / 100).toFixed(2); }
function dollarsToCents(d) { if (d === '' || d == null) return 0; const n = parseFloat(d); return isNaN(n) ? 0 : Math.round(n * 100); }
function fmtDt(s) { if (!s) return ''; return new Date(s + (s.endsWith('Z') ? '' : 'Z')).toLocaleString(); }

// Convert "YYYY-MM-DD HH:MM:SS" → "YYYY-MM-DDTHH:MM" for datetime-local input
function toLocalInput(s) {
  if (!s) return '';
  return s.replace(' ', 'T').slice(0, 16);
}
function fromLocalInput(s) {
  if (!s) return null;
  return s.replace('T', ' ') + ':00';
}

export default function EditDayModal({ entryId, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [punches, setPunches] = useState([]);
  const [audit, setAudit] = useState([]);
  const [editFields, setEditFields] = useState({});
  const [reason, setReason] = useState('');
  const [showAudit, setShowAudit] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { load(); }, [entryId]);

  async function load() {
    try {
      const r = await api.getEntryFull(entryId);
      setData(r.entry);
      setPunches(r.punches || []);
      setAudit(r.audit || []);
      setEditFields({
        day_notes: r.entry.day_notes || '',
        override_regular_seconds: r.entry.override_regular_seconds,
        override_ot1_seconds: r.entry.override_ot1_seconds,
        override_ot2_seconds: r.entry.override_ot2_seconds,
        pto_seconds: r.entry.pto_seconds || 0,
        pto_unpaid: !!r.entry.pto_unpaid,
        tip_cents: r.entry.tip_cents || 0,
        bonus_cents: r.entry.bonus_cents || 0,
        commission_cents: r.entry.commission_cents || 0,
      });
    } catch (e) { setErr(e.message); }
  }

  const totalSec = useMemo(() => {
    return punches.reduce((s, p) => {
      if (p.use_hours && p.use_hours_total_seconds) return s + p.use_hours_total_seconds;
      if (p.punch_in_at && p.punch_out_at) {
        return s + Math.max(0, (new Date(p.punch_out_at + 'Z') - new Date(p.punch_in_at + 'Z')) / 1000);
      }
      return s;
    }, 0);
  }, [punches]);

  // ── Punch edits (committed on blur to reduce request spam) ─────
  async function savePunch(p) {
    if (!reason) { setErr('Reason required for any edit'); return; }
    try {
      await api.patchPunch(p.id, {
        punch_in_at: p.punch_in_at,
        punch_out_at: p.punch_out_at,
        after_midnight: p.after_midnight,
        use_hours: p.use_hours,
        use_hours_total_seconds: p.use_hours_total_seconds,
        note: p.note,
        reason,
      });
      setErr('');
      load();
    } catch (e) { setErr(e.message); }
  }
  async function deletePunch(id) {
    if (!reason) { setErr('Reason required to delete a punch'); return; }
    if (!window.confirm('Delete this punch?')) return;
    try { await api.deletePunch(id, reason); load(); } catch (e) { setErr(e.message); }
  }
  async function addPunch() {
    if (!reason) { setErr('Reason required to add a punch'); return; }
    try {
      await api.addPunch(entryId, { punch_in_at: null, punch_out_at: null, reason });
      load();
    } catch (e) { setErr(e.message); }
  }

  // ── Save entry-level fields ─────────────────────────────────────
  async function saveEntry() {
    if (!reason) { setErr('Reason required'); return; }
    setBusy(true); setErr('');
    try {
      await api.patchEntry(entryId, { ...editFields, pto_unpaid: editFields.pto_unpaid ? 1 : 0, reason });
      setReason('');
      load();
      if (onSaved) onSaved();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  async function deleteDay() {
    if (!reason) { setErr('Reason required to delete'); return; }
    if (!window.confirm('Delete this day? It will be marked rejected (not actually removed — audit trail preserved).')) return;
    setBusy(true);
    try { await api.deleteEntry(entryId, reason); if (onSaved) onSaved(); onClose(); }
    catch (e) { setErr(e.message); setBusy(false); }
  }

  if (!data) {
    return (
      <ModalRoot onClick={onClose}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <div className="modal-body">{err || 'Loading…'}</div>
        </div>
      </ModalRoot>
    );
  }

  const dateLabel = (data.clock_in_at || '').slice(0, 10);
  return (
    <ModalRoot onClick={onClose}>
      <div className="modal-content modal-wide edit-day-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Adjusting day for {data.full_name} — {dateLabel}</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          {err && <div className="form-error">{err}</div>}

          {/* Reason — required for any edit */}
          <div className="edit-day-reason">
            <label>Reason for changes (required)
              <input type="text" value={reason} onChange={e => setReason(e.target.value)} placeholder="Required for audit log" />
            </label>
          </div>

          {/* Punches */}
          <section className="edit-day-section">
            <div className="edit-day-section-header">
              <h4>{punches.length} punch{punches.length === 1 ? '' : 'es'}</h4>
              <button className="btn-secondary" onClick={addPunch}>+ Add Punch</button>
            </div>
            {punches.map((p, idx) => (
              <PunchRow key={p.id} punch={p}
                onChange={pf => setPunches(arr => arr.map((x, i) => i === idx ? { ...x, ...pf } : x))}
                onSave={() => savePunch(p)}
                onDelete={() => deletePunch(p.id)}
              />
            ))}
            {punches.length === 0 && <div className="muted">No punches yet.</div>}
            <div className="edit-day-total">
              Computed total: <strong>{secondsToHours(totalSec)} h</strong>
            </div>
          </section>

          {/* Override Hours */}
          <section className="edit-day-section">
            <h4>Override Hours (leave blank to use computed)</h4>
            <div className="edit-day-grid edit-day-grid-3">
              <label>REG <input type="number" step="0.01" value={secondsToHours(editFields.override_regular_seconds)}
                onChange={e => setEditFields(f => ({ ...f, override_regular_seconds: hoursToSeconds(e.target.value) }))} /></label>
              <label>OT1 <input type="number" step="0.01" value={secondsToHours(editFields.override_ot1_seconds)}
                onChange={e => setEditFields(f => ({ ...f, override_ot1_seconds: hoursToSeconds(e.target.value) }))} /></label>
              <label>OT2 <input type="number" step="0.01" value={secondsToHours(editFields.override_ot2_seconds)}
                onChange={e => setEditFields(f => ({ ...f, override_ot2_seconds: hoursToSeconds(e.target.value) }))} /></label>
            </div>
          </section>

          {/* PTO + Monetary */}
          <section className="edit-day-section">
            <div className="edit-day-grid edit-day-grid-2">
              <div>
                <h4>PTO</h4>
                <label>Hours <input type="number" step="0.01" value={secondsToHours(editFields.pto_seconds)}
                  onChange={e => setEditFields(f => ({ ...f, pto_seconds: hoursToSeconds(e.target.value) || 0 }))} /></label>
                <label><input type="checkbox" checked={!!editFields.pto_unpaid}
                  onChange={e => setEditFields(f => ({ ...f, pto_unpaid: e.target.checked }))} /> Unpaid</label>
              </div>
              <div>
                <h4>Extras ($)</h4>
                <label>Tip <input type="number" step="0.01" value={centsToDollars(editFields.tip_cents)}
                  onChange={e => setEditFields(f => ({ ...f, tip_cents: dollarsToCents(e.target.value) }))} /></label>
                <label>Bonus <input type="number" step="0.01" value={centsToDollars(editFields.bonus_cents)}
                  onChange={e => setEditFields(f => ({ ...f, bonus_cents: dollarsToCents(e.target.value) }))} /></label>
                <label>Commission <input type="number" step="0.01" value={centsToDollars(editFields.commission_cents)}
                  onChange={e => setEditFields(f => ({ ...f, commission_cents: dollarsToCents(e.target.value) }))} /></label>
              </div>
            </div>
          </section>

          {/* Day Notes */}
          <section className="edit-day-section">
            <h4>Day Notes</h4>
            <textarea rows={3} value={editFields.day_notes || ''}
              onChange={e => setEditFields(f => ({ ...f, day_notes: e.target.value }))} />
          </section>

          {/* Audit Log */}
          <section className="edit-day-section">
            <button className="btn-link" onClick={() => setShowAudit(s => !s)}>
              {showAudit ? '▾' : '▸'} View Audit Log ({audit.length})
            </button>
            {showAudit && (
              <div className="edit-day-audit">
                {audit.length === 0 && <div className="muted">No edits yet.</div>}
                {audit.map(a => (
                  <div key={a.id} className="edit-day-audit-row">
                    <div className="muted">{fmtDt(a.edited_at)} · {a.edited_by_name || 'Unknown'}</div>
                    <div><code>{a.field}</code>: <span className="muted">{a.old_value ?? '∅'}</span> → <strong>{a.new_value ?? '∅'}</strong></div>
                    {a.reason && <div className="edit-day-audit-reason">"{a.reason}"</div>}
                  </div>
                ))}
              </div>
            )}
          </section>

          <div className="form-actions">
            <button className="btn-danger" onClick={deleteDay} disabled={busy}>Delete Day</button>
            <button className="btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn-primary" onClick={saveEntry} disabled={busy || !reason}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </ModalRoot>
  );
}

function PunchRow({ punch, onChange, onSave, onDelete }) {
  return (
    <div className="punch-row">
      <div className="punch-grid">
        <label>In
          <input type="datetime-local" value={toLocalInput(punch.punch_in_at)}
            onChange={e => onChange({ punch_in_at: fromLocalInput(e.target.value) })}
            onBlur={onSave} disabled={punch.use_hours} />
        </label>
        <label>Out
          <input type="datetime-local" value={toLocalInput(punch.punch_out_at)}
            onChange={e => onChange({ punch_out_at: fromLocalInput(e.target.value) })}
            onBlur={onSave} disabled={punch.use_hours} />
        </label>
        <label className="punch-flag">
          <input type="checkbox" checked={!!punch.after_midnight}
            onChange={e => { onChange({ after_midnight: e.target.checked }); setTimeout(onSave, 0); }} />
          After Midnight
        </label>
        <label className="punch-flag">
          <input type="checkbox" checked={!!punch.use_hours}
            onChange={e => { onChange({ use_hours: e.target.checked }); setTimeout(onSave, 0); }} />
          Manual hours only
        </label>
        {punch.use_hours && (
          <label>Hours
            <input type="number" step="0.01" value={(punch.use_hours_total_seconds || 0) / 3600}
              onChange={e => onChange({ use_hours_total_seconds: Math.round((parseFloat(e.target.value) || 0) * 3600) })}
              onBlur={onSave} />
          </label>
        )}
        <button className="btn-icon" onClick={onDelete} title="Delete punch">×</button>
      </div>
      <label className="punch-note">Note
        <input type="text" value={punch.note || ''}
          onChange={e => onChange({ note: e.target.value })}
          onBlur={onSave} />
      </label>
      {punch.in_ip && <div className="muted punch-ip">IP: {punch.in_ip}{punch.out_ip && punch.out_ip !== punch.in_ip ? ` / ${punch.out_ip}` : ''}</div>}
    </div>
  );
}
